import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { STAGES, PRIORIDADES, ORIGENS, fmtDate, origemLabel, slaInfo } from "@/lib/constants";
import { Page, TicketStats, StageBadge, PrioBadge, Empty, SlaBadge, useNow, pulseClass } from "@/components/ui-z";
import { useTicketActions } from "@/components/TicketActions";

const STAGE_CARD = { abertura: "card-info", t1_triagem: "card-warning", t2_atendimento_interno: "card-purple", t3_responsavel_tecnico: "card-indigo", resolucao: "card-success" };
const SLA_ROW = { breached: "table-danger", warning: "table-warning" };
const stop = (e) => e.stopPropagation();

// Colunas ordenáveis da lista (como no DataTables): valor de comparação de cada uma
const collator = new Intl.Collator("pt-BR", { numeric: true, sensitivity: "base" });
const STAGE_ORDER = Object.fromEntries(STAGES.map((s, i) => [s.key, i]));
const PRIO_ORDER = Object.fromEntries(PRIORIDADES.map((p, i) => [p.key, i]));
const COLUMNS = [
  { key: "codigo", label: "Código", value: (t) => t.codigo },
  { key: "titulo", label: "Título", value: (t) => t.titulo },
  { key: "solicitante", label: "Solicitante", value: (t) => t.solicitante_nome },
  { key: "origem", label: "Origem", value: (t) => origemLabel(t.origem) },
  { key: "etapa", label: "Etapa", value: (t) => STAGE_ORDER[t.stage] ?? 99 },
  { key: "prioridade", label: "Prioridade", value: (t) => PRIO_ORDER[t.prioridade] ?? 99 },
  { key: "responsavel", label: "Responsável", value: (t) => t.assigned_to_name || "" },
  { key: "prazo", label: "Prazo", value: (t, now) => slaInfo(t, now).left },
  { key: "atualizado", label: "Atualizado", value: (t) => new Date(t.updated_at).getTime() },
];
const compare = (a, b) => (typeof a === "number" && typeof b === "number" ? a - b : collator.compare(String(a ?? ""), String(b ?? "")));

const SortHeader = ({ col, sort, onSort }) => {
  const dir = sort.key === col.key ? sort.dir : null;
  const next = dir === "asc" ? "descendente" : "ascendente";
  return (
    <th className={dir ? `sorting_${dir}` : "sorting"} tabIndex={0} role="button" data-testid={`sort-${col.key}`}
      aria-sort={dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none"} aria-label={`${col.label}: ativar para ordenar de forma ${next}`}
      onClick={() => onSort(col.key)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSort(col.key); } }}>
      {col.label}
    </th>
  );
};

// Data local (YYYY-MM-DD), mesmo formato do <input type="date">
const localDay = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const today = () => localDay(Date.now());

const Filters = ({ f, setF, users, col = "" }) => {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <>
      <div className={`form-group ${col ? "col-xl-3 col-lg-4 col-md-6 col-12" : ""}`}>
        <div className="input-group input-group-sm">
          <input data-testid="filter-day" type="date" className="form-control" title="Dia de abertura" value={f.day} max={today()} onChange={set("day")} />
          <div className="input-group-append">
            <button type="button" data-testid="filter-day-today" className={`btn btn-default ${f.day === today() ? "active" : ""}`} title="Hoje" onClick={() => setF({ ...f, day: today() })}>Hoje</button>
            <button type="button" data-testid="filter-day-all" className={`btn btn-default ${f.day ? "" : "active"}`} title="Todos os dias" onClick={() => setF({ ...f, day: "" })}>Todos</button>
          </div>
        </div>
      </div>
      <div className={`form-group ${col}`}>
        <input data-testid="filter-search" className="form-control form-control-sm" placeholder="Buscar..." value={f.q} onChange={set("q")} />
      </div>
      <div className={`form-group ${col}`}>
        <select data-testid="filter-stage" className="form-control form-control-sm" value={f.stage} onChange={set("stage")}>
          <option value="">Todas etapas</option><option value="andamento">Em andamento (todas menos Fechado)</option>{STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>
      <div className={`form-group ${col}`}>
        <select data-testid="filter-priority" className="form-control form-control-sm" value={f.prio} onChange={set("prio")}>
          <option value="">Todas prioridades</option>{PRIORIDADES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </div>
      <div className={`form-group ${col}`}>
        <select data-testid="filter-origem" className="form-control form-control-sm" value={f.origem} onChange={set("origem")}>
          <option value="">Todas origens</option>{ORIGENS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>
      <div className={`form-group ${col}`}>
        <select data-testid="filter-assignee" className="form-control form-control-sm" value={f.assignee} onChange={set("assignee")}>
          <option value="">Todos responsáveis</option><option value="__none">Não atribuído</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div className={`form-group ${col}`}>
        <select data-testid="filter-sla" className="form-control form-control-sm" value={f.sla} onChange={set("sla")}>
          <option value="">Todos prazos</option><option value="risco">Em risco ou atrasados</option><option value="warning">Perto de estourar</option><option value="breached">Atrasados</option>
          <option value="ok">No prazo</option><option value="met">Cumpridos</option><option value="missed">Resolvidos fora do prazo</option>
        </select>
      </div>
    </>
  );
};

const ViewToggle = ({ view, setView }) => (
  <div className="btn-group btn-group-sm mr-2">
    <button type="button" data-testid="view-table-btn" title="Tabela" className={`btn btn-default ${view === "table" ? "active" : ""}`} onClick={() => setView("table")}><i className="fas fa-list" /></button>
    <button type="button" data-testid="view-kanban-btn" title="Kanban" className={`btn btn-default ${view === "kanban" ? "active" : ""}`} onClick={() => setView("kanban")}><i className="fas fa-columns" /></button>
  </div>
);

export default function ChamadosList() {
  const { tickets, users } = useStore();
  const { setWrapperClass } = useOutletContext();
  const [sp] = useSearchParams();
  const [view, setView] = useState("table");
  const now = useNow();
  const actions = useTicketActions();
  const [f, setF] = useState({ day: [...sp.keys()].length ? "" : today(), q: sp.get("q") || "", stage: sp.get("stage") || "", prio: "", origem: "", assignee: "", sla: sp.get("sla") || "" });

  // Busca/atalhos do header mudam a query string sem remontar a página
  useEffect(() => {
    setF((p) => ({ ...p, day: [...sp.keys()].length ? "" : p.day, q: sp.get("q") || "", stage: sp.get("stage") || "", sla: sp.get("sla") || "" }));
  }, [sp]);

  // O Kanban do AdminLTE depende da classe "kanban" no .content-wrapper
  useEffect(() => {
    setWrapperClass(view === "kanban" ? "kanban" : "");
    return () => setWrapperClass("");
  }, [view, setWrapperClass]);

  const [sort, setSort] = useState({ key: null, dir: "asc" });
  // Como no DataTables: 1º clique ordena ascendente, os seguintes alternam asc/desc
  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const rows = useMemo(() => {
    const q = f.q.toLowerCase();
    return tickets.filter((t) =>
      (!f.day || localDay(t.created_at) === f.day) &&
      (!f.stage || (f.stage === "andamento" ? t.status !== "resolvido" : t.stage === f.stage)) &&
      (!f.prio || t.prioridade === f.prio) &&
      (!f.origem || t.origem === f.origem) &&
      (!f.sla || (f.sla === "risco" ? ["warning", "breached"].includes(slaInfo(t, now).state) : slaInfo(t, now).state === f.sla)) &&
      (!f.assignee || (f.assignee === "__none" ? !t.assigned_to : t.assigned_to === f.assignee)) &&
      (!q || [t.codigo, t.titulo, t.descricao, t.solicitante_nome, t.origem_ref, t.categoria].some((v) => v && v.toLowerCase().includes(q)))
    );
  }, [tickets, f, now]);

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sort.key);
    if (!col) return rows;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => factor * compare(col.value(a, now), col.value(b, now)));
  }, [rows, sort, now]);

  // Arrastar e soltar entre colunas (como no Kanban do AdminLTE): soltar abre a transferência/resolução já com a etapa de destino
  const [drag, setDrag] = useState(null);
  const [dropOn, setDropOn] = useState(null);
  const canDrop = (stage) => drag && drag.stage !== stage;
  const onDrop = (stage) => (e) => {
    e.preventDefault();
    const t = drag;
    setDrag(null);
    setDropOn(null);
    if (!t || t.stage === stage) return;
    if (stage === "resolucao") actions.showResolve(t);
    else actions.showTransfer(t, stage);
  };

  const newBtn = <Link to="/chamados/novo" data-testid="list-new-ticket-btn" className="btn btn-primary btn-sm"><i className="fas fa-plus mr-1" />Novo chamado</Link>;

  if (view === "kanban") {
    return (
      <Page title="Chamados" crumbs={[["Chamados"]]}>
        <div className="card card-row card-default">
          <div className="card-header">
            <h3 className="card-title">Filtros</h3>
            <div className="card-tools"><ViewToggle view={view} setView={setView} />{newBtn}</div>
          </div>
          <div className="card-body">
            <Filters f={f} setF={setF} users={users} />
            <p className="text-muted text-sm mb-0" data-testid="results-count">{rows.length} chamado(s)</p>
          </div>
        </div>
        {STAGES.map((s) => {
          const items = rows.filter((t) => t.stage === s.key);
          return (
            <div key={s.key} className={`card card-row ${STAGE_CARD[s.key]}`} data-testid={`kanban-col-${s.key}`}>
              <div className="card-header">
                <h3 className="card-title">{s.label}</h3>
                <div className="card-tools"><span className="badge badge-light">{items.length}</span></div>
              </div>
              <div className={`card-body kanban-lane ${canDrop(s.key) ? "kanban-droppable" : ""} ${dropOn === s.key && canDrop(s.key) ? "kanban-over" : ""}`}
                onDragOver={(e) => { if (canDrop(s.key)) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropOn !== s.key) setDropOn(s.key); } }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropOn((d) => (d === s.key ? null : d)); }}
                onDrop={onDrop(s.key)}>
                {items.map((t) => {
                  const sla = slaInfo(t, now).state;
                  // Chamado fechado não muda de etapa (mesma regra do menu de ações)
                  const movable = t.status !== "resolvido";
                  return (
                    <div key={t.id} className={`card card-outline ${sla === "breached" ? "card-danger" : sla === "warning" ? "card-warning" : "card-light"} ${pulseClass(t)} ${movable ? "kanban-card" : ""} ${drag?.id === t.id ? "kanban-dragging" : ""}`}
                      data-testid={`kanban-card-${t.codigo}`} onContextMenu={(e) => actions.open(t, e)}
                      draggable={movable}
                      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", t.codigo); setDrag(t); }}
                      onDragEnd={() => { setDrag(null); setDropOn(null); }}>
                      <div className="card-header">
                        <h5 className="card-title text-truncate" style={{ maxWidth: 200 }}>{t.titulo}</h5>
                        <div className="card-tools">
                          <Link to={`/chamados/${t.id}`} className="btn btn-tool btn-link" onClick={stop}>{t.codigo}</Link>
                          <button type="button" className="btn btn-tool" title="Histórico" onClick={(e) => actions.showHistory(t, e)}><i className="fas fa-history" /></button>
                        </div>
                      </div>
                      <div className="card-body">
                        <p className="mb-2 text-sm">{t.solicitante_nome} &middot; {t.assigned_to_name || "sem responsável"}</p>
                        <PrioBadge prio={t.prioridade} /> <SlaBadge ticket={t} now={now} testId={`kanban-sla-${t.codigo}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {actions.element}
      </Page>
    );
  }

  return (
    <Page title="Chamados" crumbs={[["Chamados"]]}>
      <TicketStats tickets={rows} now={now} />
      <div className="card card-default">
        <div className="card-header">
          <h3 className="card-title"><i className="fas fa-filter mr-1" />Filtros</h3>
          <div className="card-tools">
            <button type="button" className="btn btn-tool" data-card-widget="collapse"><i className="fas fa-minus" /></button>
          </div>
        </div>
        <div className="card-body pb-0">
          <div className="row">
            <Filters f={f} setF={setF} users={users} col="col-xl col-lg-3 col-md-4 col-6" />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">{newBtn}</div>
          <div className="card-tools"><ViewToggle view={view} setView={setView} /></div>
        </div>
        <div className="card-body table-responsive p-0">
          {rows.length === 0 ? <Empty testId="tickets-empty" text="Nenhum chamado encontrado." /> : (
            <table className="table table-hover table-striped text-nowrap dataTable" data-testid="tickets-table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => <SortHeader key={c.key} col={c} sort={sort} onSort={onSort} />)}
                  <th className="text-right sorting_disabled">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((t) => (
                  <tr key={t.id} data-testid={`ticket-row-${t.codigo}`} className={`${SLA_ROW[slaInfo(t, now).state] || ""} ${pulseClass(t)}`} onContextMenu={(e) => actions.open(t, e)}>
                    <td><Link to={`/chamados/${t.id}`} onClick={stop}><b>{t.codigo}</b></Link></td>
                    <td className="text-truncate" style={{ maxWidth: 260 }}><Link to={`/chamados/${t.id}`} className="text-reset" onClick={stop}>{t.titulo}</Link></td>
                    <td>{t.solicitante_nome}</td>
                    <td>{origemLabel(t.origem)}</td>
                    <td><StageBadge stage={t.stage} /></td>
                    <td><PrioBadge prio={t.prioridade} /></td>
                    <td>{t.assigned_to_name || "—"}</td>
                    <td><SlaBadge ticket={t} now={now} testId={`ticket-sla-${t.codigo}`} /></td>
                    <td>{fmtDate(t.updated_at)}</td>
                    <td className="text-right">
                      <div className="btn-group btn-group-sm">
                        <button type="button" className="btn btn-default" title="Histórico" data-testid={`ticket-history-${t.codigo}`} onClick={(e) => actions.showHistory(t, e)}><i className="fas fa-history" /></button>
                        <button type="button" className="btn btn-default" title="Mais ações" data-testid={`ticket-actions-${t.codigo}`} onClick={(e) => actions.open(t, e)}><i className="fas fa-ellipsis-v" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {actions.element}
    </Page>
  );
}
